import React from 'react';
import { Input } from 'reactstrap';

import { formatTimeString } from '~libs/dayjs';
import { isFullDay, isLockedOverride, TimeBandRow } from '~support-at-home/lib/timeBands';

import Icon, { faExclamationTriangle } from '~components/icon';
import { Column, EditorRenderer, Row } from '~components/table-view';
import ResetBandAction from '~support-at-home/components/config/time-bands/ResetBandAction';

const asBandRow = (row: Row): TimeBandRow => row as TimeBandRow;

/** Said on the flag itself, so the times carry their own explanation wherever the row is read. */
const overrideTitle = (band: TimeBandRow): string =>
  `${band.name} applies for its whole day on the platform, but this company is priced on these times. ` +
  'They cannot be changed or cleared from this tab.';

/**
 * `ColumnType` has no time type, so the two editable columns are custom ones with their own editor.
 * A native time input is used rather than the shared `FormInput.Time` picker: that picker flags any
 * minute outside the company's appointment interval as invalid, and band times are set to the
 * minute (a company moving its evening band to 8:01 PM is the case this feature exists for).
 */
const timeEditor =
  (columnId: string): EditorRenderer =>
  // Column editor factory, not a component - same shape as the shared editors in columnEditors.tsx.
  // eslint-disable-next-line react/display-name
  (value, row, onChange, _context, isDisabled = false) => (
    <Input
      id={`${row.tag}-${columnId}`}
      name={`${row.tag}-${columnId}`}
      type="time"
      className="cell"
      disabled={isDisabled}
      value={typeof value === 'string' ? value : ''}
      aria-label={`${asBandRow(row).name} ${columnId === 'start_time' ? 'start' : 'end'} time`}
      data-cy={`sah-time-bands-${columnId.replace('_', '-')}-input-${row.tag}`}
      onChange={({ target }) => onChange(target.value)}
    />
  );

/**
 * Sentinel bands have no range to show, so they say what they actually do instead. Wrapped in the
 * same `cell readonly` element the shared read-only renderer uses, so the cells line up.
 *
 * A locked band that holds times of its own is the exception. Claims are priced on those times, so
 * saying "All day" over the top of them would report the platform's intent as though it were this
 * company's pricing. It reports the real times, and flags them, instead. The call is taken from the
 * row's own effective times: the platform times are the whole-day sentinel on every locked band and
 * so can never tell an overridden one from a clean one.
 */
// eslint-disable-next-line react/display-name
const readOnlyTime = (isStart: boolean) => (value: unknown, row: Row) => {
  const band = asBandRow(row);
  const display = formatTimeString(value) || '-';

  if (isLockedOverride(band)) {
    return (
      <div
        className="cell readonly text-danger"
        data-cy={`sah-time-bands-override-${isStart ? 'start' : 'end'}-${band.tag}`}
      >
        {isStart && <Icon icon={faExclamationTriangle} className="me-1" title={overrideTitle(band)} />}
        {display}
      </div>
    );
  }

  if (isFullDay(band.default_start_time, band.default_end_time)) {
    return <div className="cell readonly">{isStart ? 'All day' : '-'}</div>;
  }

  return <div className="cell readonly">{display}</div>;
};

const columnsFactory = (onReset: (row: TimeBandRow) => void, canUpdate: boolean, showIssues: boolean): Column[] => [
  {
    id: 'name',
    header: 'Time Band',
    type: 'readonly',
  },
  {
    id: 'days',
    header: 'Days',
    type: 'readonly',
    style: { width: '120px' },
  },
  {
    id: 'start_time',
    header: 'Start',
    type: 'custom',
    style: { width: '150px' },
    isEditable: (row) => asBandRow(row).is_editable,
    renderEditor: timeEditor('start_time'),
    renderReadOnly: readOnlyTime(true),
  },
  {
    id: 'end_time',
    header: 'End',
    type: 'custom',
    style: { width: '150px' },
    isEditable: (row) => asBandRow(row).is_editable,
    renderEditor: timeEditor('end_time'),
    renderReadOnly: readOnlyTime(false),
  },
  {
    id: 'platform_default',
    header: 'Platform Default',
    type: 'readonly',
    style: { width: '180px' },
    deriveValue: (row) => {
      const { default_start_time, default_end_time } = asBandRow(row);
      if (isFullDay(default_start_time, default_end_time)) return 'All day';
      return `${formatTimeString(default_start_time)} to ${formatTimeString(default_end_time)}`;
    },
  },
  {
    // A rejection reason is a sentence, not a value, so the column is bounded and wraps rather than
    // widening the table past the viewport. `text-wrap` is the same utility the supplier invoice
    // columns use for their long cells.
    id: 'issue',
    header: 'Issue',
    type: 'readonly',
    style: { width: '280px' },
    classNames: { column: 'text-wrap' },
    showColumn: showIssues,
    renderReadOnly: (value) => (
      <div className="cell readonly text-wrap text-danger" data-cy="sah-time-bands-issue">
        {value || ''}
      </div>
    ),
  },
  {
    // An action, so it takes the right-most column and no header - the same shape the budgets list
    // uses for its own action and badge columns. The per-row control carries its own accessible
    // name, so nothing is lost by leaving the header text out.
    id: 'reset',
    header: '',
    type: 'custom',
    style: { width: '60px' },
    classNames: { header: 'cell-align-center', column: 'cell-align-center' },
    component: ResetBandAction,
    componentProps: (row: Row) => ({ row, onReset, canUpdate }),
  },
];

export default columnsFactory;
